import { forwardRef, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cx } from '../../lib/cx';

const CONTROL =
  'form-input w-full rounded border bg-surface px-2.5 py-1.5 text-[13px] text-ink placeholder:text-ink-subtle ' +
  'focus:ring-0 disabled:bg-surface-sunken disabled:text-ink-subtle';

/**
 * The control's border colour, as a single exclusive choice rather than a base colour with
 * an error colour layered on top.
 *
 * Layering does not work here, and the way it fails is silent: Tailwind emits utilities in
 * its own order, not the order they appear in a `class` attribute, so `border-hairline-strong`
 * and `border-danger` on the same element are decided by whichever happens to sit lower in
 * the generated stylesheet. That is how a field could carry `aria-invalid`, show its error
 * message, and still draw a plain grey border — which is exactly what it did.
 *
 * An invalid field keeps its red border while focused. The focus ring still shows focus,
 * and swapping the border back to blue the moment someone clicks in to fix the problem
 * would remove the marker at the one time they are looking straight at it.
 */
function borderFor(hasError: boolean): string {
  return hasError
    ? 'border-danger focus:border-danger'
    : 'border-hairline-strong focus:border-primary';
}

interface FieldShellProps {
  id: string;
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  children: ReactNode;
  className?: string;
}

function FieldShell({ id, label, hint, error, children, className }: FieldShellProps) {
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-[12px] font-medium text-ink-muted">
        {label}
      </label>
      {children}
      {hint && !error ? (
        <p id={`${id}-hint`} className="text-2xs leading-4 text-ink-subtle">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-2xs leading-4 text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  /**
   * Mark the control at fault when its message lives somewhere else — the Transfers date
   * range, where one message answers for both From and To (see the "Filter bar" section).
   *
   * It exists so that case still goes through `borderFor` below. Written as an extra
   * `border-danger` class at the call site instead, it lost to the neutral border every
   * time: the field said its piece in red underneath and drew a perfectly ordinary
   * outline, which is the exact failure this component was built to make impossible.
   * Pair it with `aria-describedby` pointing at the shared message.
   */
  invalid?: boolean;
  wrapperClassName?: string;
}

/**
 * The ref is forwarded so a form can move focus to the first field at fault when its
 * submit button is pressed on incomplete input (see the "Admin Interface" section, "A
 * control never refuses in silence").
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, invalid, className, wrapperClassName, ...rest },
  ref,
) {
  const id = useId();
  const atFault = Boolean(error) || Boolean(invalid);
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} className={wrapperClassName}>
      <input
        id={id}
        ref={ref}
        {...rest}
        aria-invalid={atFault ? true : undefined}
        aria-describedby={
          error ? `${id}-error` : (rest['aria-describedby'] ?? (hint ? `${id}-hint` : undefined))
        }
        className={cx(CONTROL, borderFor(atFault), className)}
      />
    </FieldShell>
  );
});

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  wrapperClassName?: string;
}

export function SelectField({
  label,
  hint,
  error,
  className,
  wrapperClassName,
  children,
  ...rest
}: SelectFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} error={error} className={wrapperClassName}>
      <select
        id={id}
        {...rest}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={cx(CONTROL, 'form-select', borderFor(Boolean(error)), className)}
      >
        {children}
      </select>
    </FieldShell>
  );
}

export interface TextAreaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField({ label, hint, error, className, ...rest }, ref) {
    const id = useId();
    return (
      <FieldShell id={id} label={label} hint={hint} error={error}>
        <textarea
          id={id}
          ref={ref}
          {...rest}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          className={cx(CONTROL, 'form-textarea', borderFor(Boolean(error)), className)}
        />
      </FieldShell>
    );
  },
);
